import React from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Typography, Button, Container as MuiContainer,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { LandingContainer } from '../home/HomePage.styles';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { DeckVisualPanel } from '../../shared/components/DeckVisuals';
import SEO from '../../shared/components/SEO';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';
import { useCases } from './useCasesData';

/**
 * /solutions/:slug: BOFU pages targeting high-intent searches like
 * "AI IC memo builder", "AI market sizing tool". One component, slug-
 * driven from useCasesData.js. Layout intentionally narrower than Compare
 * pages, since these are conversion-focused single-flow narratives.
 */

const ACCENT = '#B83B1D';
const INK = '#2E140A';

// The primary CTA label, matching the standing CTA in SiteHeader and the one
// the rest of the site asks for. Used in the hero and repeated verbatim at the
// close, so the page asks for one thing in one wording.
const PRIMARY_CTA_LABEL = 'See it on your own deck';

// Hero proof panel per slug, in the shared deck-visual language. Abstract by
// design: these illustrate the shape of the output, they do not pose as a
// screenshot of a real client deliverable.
const HERO_VISUALS = {
  'ai-ic-memo-builder': 'citations',
  'ai-market-sizing': 'charts',
};

// FAQ for the use-case pages, pulled by id from FAQ_REPOSITORY (the same copy
// the support widget serves) so the two can never drift. Ordered for a reader
// who arrived on a specific deliverable: does the research hold, how does it
// run, how is it different, then the commercial questions. It ends on `refund`,
// which carries the 30-day risk reversal, directly above the closing CTA.
const USE_CASE_FAQ_IDS = [
  'research-quality',
  'how-works',
  'different-from-chatgpt',
  'frameworks',
  'powerpoint-versions',
  'security',
  'collaboration',
  'pricing',
  'refund',
];

const USE_CASE_FAQ = USE_CASE_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

export default function UseCasePage() {
  const { slug } = useParams();
  const theme = useTheme();
  const data = useCases[slug];

  if (!data) return <Navigate to="/" replace />;

  const url = `https://re-stack.com/solutions/${slug}`;

  // HowTo schema is a strong fit for "do X with Restack" use case pages:
  // it's eligible for step-by-step rich results in Google's SERP. The FAQPage
  // block below it mirrors the FAQ section the page actually renders.
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: data.metaTitle,
      description: data.metaDescription,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      step: data.workflow.map((s) => ({
        '@type': 'HowToStep',
        position: parseInt(s.step, 10),
        name: s.title,
        text: s.description,
      })),
      tool: {
        '@type': 'SoftwareApplication',
        name: 'Restack',
        url: 'https://re-stack.com/',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Microsoft PowerPoint',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: USE_CASE_FAQ.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    },
  ];

  // One section rhythm for every band on the page. Previously each block set
  // its own bottom margin (4/5, 5/6, 5/7), which read as drifting spacing.
  const sectionSx = { mb: { xs: 5, md: 7 } };
  const headingSx = {
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 700,
    fontSize: { xs: '1.4rem', md: '1.7rem' },
    mb: 2,
    color: 'text.primary',
    letterSpacing: '-0.02em',
  };
  const proseSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '1rem', md: '1.06rem' },
    lineHeight: 1.7,
    color: alpha(theme.palette.text.primary, 0.8),
  };

  return (
    <LandingContainer data-theme="light" sx={{ position: 'relative' }}>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        canonical={`/solutions/${slug}`}
        ogType="article"
        schema={schema}
      />

      {/* Shared site header: same nav, same standing CTA, and the skip link
          every other marketing page gets. This replaced a page-local AppBar
          that carried its own "Back to Home" and "Request a Demo" pair. */}
      <SiteHeader />

      <MuiContainer
        component="main"
        id="main-content"
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 760,
          pt: { xs: 'calc(56px + 2.5rem)', md: 'calc(64px + 3.5rem)' },
          pb: { xs: 6, md: 10 },
        }}
      >
        {/* Hero */}
        <Box sx={{ textAlign: 'center', ...sectionSx }}>
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: ACCENT, mb: 2 }}>
            {data.hero.eyebrow}
          </Typography>
          <Typography component="h1" sx={{ fontFamily: BRAND_SANS_FONT, fontWeight: 700, fontSize: { xs: '2rem', sm: '2.6rem', md: '3.1rem' }, lineHeight: 1.1, letterSpacing: '-0.025em', color: 'text.primary', mb: 2.5 }}>
            {data.hero.headline}
          </Typography>
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1.05rem', md: '1.18rem' }, color: alpha(theme.palette.text.primary, 0.7), maxWidth: 580, mx: 'auto', lineHeight: 1.55 }}>
            {data.hero.subhead}
          </Typography>
          {/* The page's one ask, repeated verbatim at the close. Previously the
              first CTA a reader met was at the bottom of the page. */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, md: 3.5 } }}>
            <DemoCta label={PRIMARY_CTA_LABEL} />
          </Box>
        </Box>

        {/* Hero visual. Decorative: the surrounding copy says the same thing,
            so it is hidden from assistive tech rather than given fake alt. */}
        {HERO_VISUALS[slug] && (
          <Box aria-hidden="true" sx={sectionSx}>
            <DeckVisualPanel visual={HERO_VISUALS[slug]} variant="standalone" />
          </Box>
        )}

        {/* Stats card: pulls eye to ROI before reader scrolls */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)' }, gap: { xs: 1.25, md: 2 }, ...sectionSx, p: { xs: 2, md: 2.75 }, borderRadius: '16px', backgroundColor: alpha(ACCENT, 0.04), border: `1px solid ${alpha(ACCENT, 0.12)}` }}>
          {data.stats.map((stat) => (
            <Box key={stat.label} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.1rem' }, color: ACCENT, lineHeight: 1, mb: 0.75, letterSpacing: '-0.02em' }}>
                {stat.value}
              </Typography>
              <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.72rem', md: '0.78rem' }, color: alpha(theme.palette.text.primary, 0.65), letterSpacing: '0.02em', lineHeight: 1.3 }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Problem */}
        <Box component="section" sx={sectionSx}>
          <Typography component="h2" sx={headingSx}>
            {data.problem.title}
          </Typography>
          <Typography sx={proseSx}>
            {data.problem.body}
          </Typography>
        </Box>

        {/* Solution */}
        <Box component="section" sx={sectionSx}>
          <Typography component="h2" sx={headingSx}>
            {data.solution.title}
          </Typography>
          <Typography sx={proseSx}>
            {data.solution.body}
          </Typography>
        </Box>

        {/* Workflow steps */}
        <Box component="section" sx={sectionSx}>
          <Typography component="h2" sx={{ ...headingSx, mb: 3 }}>
            How it works
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {data.workflow.map((step) => (
              <Box key={step.step} sx={{ display: 'flex', gap: { xs: 2, md: 2.5 }, alignItems: 'flex-start' }}>
                <Box aria-hidden="true" sx={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', backgroundColor: ACCENT, color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: BRAND_SANS_FONT, fontWeight: 700, fontSize: '0.95rem', mt: 0.25 }}>
                  {step.step}
                </Box>
                <Box>
                  <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontWeight: 700, fontSize: { xs: '1.05rem', md: '1.15rem' }, mb: 0.75, color: 'text.primary', letterSpacing: '-0.015em' }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.6, color: alpha(theme.palette.text.primary, 0.7) }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Outputs list: what you actually get */}
        <Box component="section" sx={{ ...sectionSx, p: { xs: 2.75, md: 3.5 }, borderRadius: '16px', border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, backgroundColor: '#ffffff' }}>
          <Typography component="h2" sx={{ ...headingSx, fontSize: { xs: '1.15rem', md: '1.3rem' }, letterSpacing: '-0.015em' }}>
            What you get
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {data.outputs.map((output, i) => (
              <Box key={i} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.85, fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.92rem', md: '0.97rem' }, lineHeight: 1.5, color: alpha(theme.palette.text.primary, 0.8), borderBottom: i === data.outputs.length - 1 ? 'none' : `1px solid ${alpha(theme.palette.text.primary, 0.05)}` }}>
                <Box aria-hidden="true" sx={{ flexShrink: 0, mt: 0.4, width: 14, height: 14, borderRadius: '50%', backgroundColor: alpha(ACCENT, 0.12), color: ACCENT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 700 }}>✓</Box>
                {output}
              </Box>
            ))}
          </Box>
        </Box>

        {/* FAQ: the objections a reader raises before trialling anything,
            answered on the page instead of only inside the support widget.
            Copy is reused verbatim from FAQ_REPOSITORY and emitted as FAQPage
            JSON-LD above; the last entry carries the 30-day guarantee. */}
        <Box component="section" sx={sectionSx}>
          <Typography component="h2" sx={{ ...headingSx, mb: 2.5 }}>
            Before you trial it
          </Typography>
          {USE_CASE_FAQ.map(({ id, question, answer }) => (
            <Accordion
              key={id}
              disableGutters
              elevation={0}
              square
              sx={{
                backgroundColor: 'transparent',
                borderBottom: `1px solid ${alpha(INK, 0.08)}`,
                '&::before': { display: 'none' },
              }}
            >
              {/* component="span": MUI v7 already wraps the summary in a
                  heading, so a nested heading would break the outline. */}
              <AccordionSummary
                expandIcon={<ExpandMoreRounded sx={{ color: alpha(INK, 0.45) }} />}
                sx={{
                  px: 0.5,
                  minHeight: 52,
                  '& .MuiAccordionSummary-content': { my: 1 },
                  '&:focus-visible': {
                    outline: `2px solid ${ACCENT}`,
                    outlineOffset: '-2px',
                    borderRadius: '6px',
                  },
                }}
              >
                <Typography component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.95rem', fontWeight: 600, letterSpacing: '-0.01em', color: 'text.primary' }}>
                  {question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.9rem', lineHeight: 1.65, color: alpha(theme.palette.text.primary, 0.72) }}>
                  {answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Closing line */}
        <Box sx={{ ...sectionSx, p: { xs: 2.75, md: 3.5 }, borderRadius: '16px', borderLeft: `3px solid ${ACCENT}`, backgroundColor: alpha(ACCENT, 0.04) }}>
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '0.98rem', md: '1.05rem' }, lineHeight: 1.65, color: 'text.primary', fontStyle: 'italic' }}>
            {data.closing}
          </Typography>
        </Box>

        {/* Closing CTA: same ask, same wording as the hero. */}
        <Box sx={{ textAlign: 'center' }}>
          <DemoCta label={PRIMARY_CTA_LABEL} large />
          <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.82rem', color: alpha(theme.palette.text.primary, 0.55), mt: 1.5 }}>
            We'll walk through Restack on a real deal you're working on.
          </Typography>
        </Box>
      </MuiContainer>

      <Footer />
    </LandingContainer>
  );
}

// Demo-form CTA. A real link to /demo rather than a button with an onClick, so
// it can be opened in a new tab and reads as a link to assistive tech.
function DemoCta({ label, large = false }) {
  return (
    <Button
      component={RouterLink}
      to="/demo"
      endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
      sx={{
        borderRadius: '999px',
        px: large ? 3.5 : 2.75,
        py: large ? 1.1 : 0.95,
        fontFamily: BRAND_SANS_FONT,
        fontWeight: 600,
        fontSize: large ? '0.95rem' : '0.88rem',
        letterSpacing: '-0.005em',
        textTransform: 'none',
        boxShadow: 'none',
        backgroundColor: INK,
        color: '#ffffff',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
        '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 4px 12px rgba(46, 20, 10, 0.22)' },
        '&:active': { transform: 'translateY(1px)', boxShadow: 'none' },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '3px' },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:active': { transform: 'none' },
        },
      }}
    >
      {label}
    </Button>
  );
}
